import { CheckIcon, KeyIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { ProductVideo } from '../components/marketing/ProductVideo';
import { Grid, Inline, Split, Stack } from '../components/ui';
import { marketingMedia, securityHighlights } from '../lib/marketing';

const mediaById = Object.fromEntries(marketingMedia.map((item) => [item.id, item]));

function media(id: string) {
  const item = mediaById[id];
  if (!item) throw new Error(`Missing marketing media: ${id}`);
  return item;
}

const executionFlow = [
  ['01', 'Understand', 'Confirm who is asking, which company they belong to, and what their role allows.'],
  ['02', 'Limit', 'Give the AI only the tools it needs for that person’s request.'],
  ['03', 'Check', 'Make sure every requested action is complete, valid, and allowed.'],
  ['04', 'Approve', 'Show important changes to a person before anything happens.'],
  ['05', 'Record', 'Complete the approved work and keep a clear history of the result.'],
];

const trustRoadmap = [
  'Enterprise sign-on options so larger teams can use their existing identity provider',
  'Company-controlled retention and deletion rules for inspection records and media',
  'Regional hosting choices backed by clear customer agreements',
  'Independent security reviews and formal compliance milestones as the platform grows',
];

export function SecurityPage(): JSX.Element {
  return (
    <div className="space-y-28 sm:space-y-36">
      <header className="glass-card relative overflow-hidden px-6 py-12 sm:px-10 sm:py-16 lg:px-14">
        <div
          className="absolute -left-32 -top-40 h-[32rem] w-[32rem] rounded-full bg-[radial-gradient(circle_at_center,hsl(188_86%_65%/.28),transparent_68%)] blur-2xl"
          aria-hidden
        />
        <Split
          ratio="0.82:1.18"
          gap={10}
          align="center"
          className="relative"
          primary={
            <div className="space-y-6">
              <span className="badge-soft">
                <ShieldCheckIcon className="h-4 w-4" /> Built-in safeguards
              </span>
              <div className="space-y-4">
                <h1 className="text-4xl font-semibold tracking-tight text-foreground sm:text-5xl lg:text-[3.35rem] lg:leading-[1.04]">
                  AI that moves quickly—with your people in control.
                </h1>
                <p className="text-lg text-muted-foreground">
                  DefectSnap checks who is asking, what they can see, which actions the AI may use, and when a person
                  must approve. AI prepares the work; the product’s safeguards decide what can happen.
                </p>
              </div>
              <Link to="/signup" className="btn-glass btn-primary focus-ring inline-flex text-sm">
                Start with a secure workspace
              </Link>
            </div>
          }
          secondary={<ProductVideo media={media('governed-agent-plan')} eager />}
        />
      </header>

      <section className="space-y-10">
        <div className="mx-auto max-w-3xl space-y-4 text-center">
          <span className="badge-soft">Protection at every step</span>
          <h2 className="text-3xl font-semibold text-foreground sm:text-4xl">
            Clear safeguards your team does not have to remember.
          </h2>
          <p className="text-muted-foreground">
            Access, approvals, private media, safe updates, and history are built into the product. They keep working
            even when an AI suggestion is incomplete or wrong.
          </p>
        </div>

        <Grid columns={{ base: 1, md: 2, xl: 3 }} gap={5}>
          {securityHighlights.map((section) => (
            <Stack as="article" gap={5} key={section.title} className="glass-card h-full p-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <KeyIcon className="h-5 w-5" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">{section.title}</h3>
              <ul className="space-y-3 text-sm text-muted-foreground">
                {section.items.map((item) => (
                  <Inline as="li" gap={3} align="stretch" key={item}>
                    <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                    <span>{item}</span>
                  </Inline>
                ))}
              </ul>
            </Stack>
          ))}
        </Grid>
      </section>

      <section className="space-y-10">
        <Split
          ratio="0.72:1.28"
          gap={8}
          align="end"
          primary={
            <div className="space-y-4">
              <span className="badge-soft">One safe path</span>
              <h2 className="text-3xl font-semibold text-foreground sm:text-4xl">
                What happens before AI changes anything.
              </h2>
            </div>
          }
          secondary={
            <p className="text-muted-foreground">
              The AI sees only the actions available to that person. DefectSnap checks the request again, asks for
              approval when needed, and records what happened.
            </p>
          }
        />

        <Grid as="ol" columns={{ base: 1, md: 2, xl: 5 }} gap={4}>
          {executionFlow.map(([number, title, description]) => (
            <li key={number} className="surface-panel p-5">
              <span className="text-xs font-semibold tracking-[0.2em] text-primary">{number}</span>
              <h3 className="mt-3 font-semibold text-foreground">{title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{description}</p>
            </li>
          ))}
        </Grid>
      </section>

      <section>
        <Split
          ratio="1.2:0.8"
          gap={10}
          align="center"
          primary={<ProductVideo media={media('access-and-roles')} />}
          secondary={
            <div className="space-y-5 px-2 sm:px-6">
              <span className="badge-soft">Understandable access</span>
              <h2 className="text-3xl font-semibold text-foreground">
                Rules people can understand before AI uses them.
              </h2>
              <p className="text-muted-foreground">
                Team members and reusable roles live in one access area. Each person sees the right product areas,
                and AI gets only the actions that person is allowed to use.
              </p>
            </div>
          }
        />
      </section>

      <section className="glass-card px-6 py-10 sm:px-10 lg:px-12">
        <Split
          ratio="0.7:1.3"
          gap={8}
          primary={
            <div className="space-y-4">
              <span className="badge-soft">Enterprise trust roadmap</span>
              <h2 className="text-3xl font-semibold text-foreground">Security that grows with your enterprise.</h2>
              <p className="text-sm text-muted-foreground">
                The protections above are available in DefectSnap today. The next trust milestones focus on identity,
                data controls, hosting choices, and independent proof larger customers expect.
              </p>
            </div>
          }
          secondary={
            <Stack as="ul" gap={4}>
              {trustRoadmap.map((item) => (
                <Inline as="li" gap={3} align="stretch" key={item} className="surface-panel p-5 text-sm text-muted-foreground">
                  <ShieldCheckIcon className="h-5 w-5 shrink-0 text-primary" />
                  <span>{item}</span>
                </Inline>
              ))}
            </Stack>
          }
        />
      </section>
    </div>
  );
}
